import React from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Client from './components/Client';
import Config from './components/ClientConfig';
import CustomPeriodPopover, { businessTodayIso, validateCustomPeriod } from './components/biReports/CustomPeriodPopover';

const CREDIT_TYPES = {
  0: 'Reward',
  1: 'Bonus',
  2: 'Used',
  3: 'Sent',
  4: 'Manual add',
  5: 'Manual remove',
  6: 'Refund',
  9: 'Undefined',
};

const STATUS_LABELS = {
  0: 'Registered',
  1: 'Complete',
  2: 'Complete with errors',
  3: 'Cancelled',
  4: 'InSync',
  5: 'Paid',
  6: 'Accepted',
  7: 'Ready',
  8: 'Rejected',
  9: 'Undefined',
};

const ADJUSTMENT_REASONS = [
  'Customer compensation',
  'Promotion correction',
  'Refund correction',
  'Operational mistake',
  'Abuse / invalid credit',
  'Other',
];

const PERIOD_LABELS = {
  today: 'Today',
  last_7d: '7d',
  last_30d: '30d',
  this_month: 'Month',
  last_90d: '90d',
  custom: 'Custom',
};

const CREDIT_TIME_ZONE = 'Europe/Bucharest';
// dailySummary accepts a span of 120 days, including both endpoints.
const MAX_CREDIT_PERIOD_DAYS = 121;

function validDateTimeParts(year, month, day, hour, minute, second) {
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    && date.getUTCHours() === hour
    && date.getUTCMinutes() === minute
    && date.getUTCSeconds() === second;
}

function displayDateTimeParts(year, month, day, hour, minute) {
  return [
    String(day).padStart(2, '0'),
    String(month).padStart(2, '0'),
    String(year).padStart(4, '0'),
  ].join('-') + ` ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function formatZonedDateTime(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CREDIT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}`;
}

export function formatCreditDateTime(value) {
  if (!value) return '-';

  const rawValue = String(value).trim();
  const europeanMatch = rawValue.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (europeanMatch) {
    const day = Number(europeanMatch[1]);
    const month = Number(europeanMatch[2]);
    const year = Number(europeanMatch[3]);
    const hour = Number(europeanMatch[4] || 0);
    const minute = Number(europeanMatch[5] || 0);
    const second = Number(europeanMatch[6] || 0);
    if (validDateTimeParts(year, month, day, hour, minute, second)) {
      return displayDateTimeParts(year, month, day, hour, minute);
    }
    return rawValue;
  }

  const localIsoMatch = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/,
  );
  if (localIsoMatch) {
    const year = Number(localIsoMatch[1]);
    const month = Number(localIsoMatch[2]);
    const day = Number(localIsoMatch[3]);
    const hour = Number(localIsoMatch[4] || 0);
    const minute = Number(localIsoMatch[5] || 0);
    const second = Number(localIsoMatch[6] || 0);
    if (validDateTimeParts(year, month, day, hour, minute, second)) {
      return displayDateTimeParts(year, month, day, hour, minute);
    }
    return rawValue;
  }

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return rawValue;
  return formatZonedDateTime(date);
}

export function shiftCreditReportDate(value, dayOffset) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!validDateTimeParts(year, month, day, 0, 0, 0)) return '';

  const shiftedDate = new Date(Date.UTC(year, month - 1, day));
  shiftedDate.setUTCDate(shiftedDate.getUTCDate() + Number(dayOffset || 0));

  return [
    shiftedDate.getUTCFullYear(),
    String(shiftedDate.getUTCMonth() + 1).padStart(2, '0'),
    String(shiftedDate.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

class ClientCreditManagement extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      clientApi: new Client('none'),

      email: '',
      searchEmail: '',
      selectedClient: null,

      periodMode: 'this_month',
      dateFrom: '',
      dateTo: '',
      customPeriodOpen: false,
      draftDateFrom: '',
      draftDateTo: '',
      customPeriodError: '',
      history: [],
      historyFilter: 'all',

      allClientSummary: {
        rewarded: 0,
        used: 0,
        net: 0,
        transactions: 0,
      },
      allClientDailyRows: [],
      creditBalanceAmount: null,
      creditBalanceDate: '',
      creditBalanceEstimated: false,
      loadingCreditBalance: false,
      creditBalanceError: '',

      rewardExceptionsOpen: false,
      rewardExceptionsDate: '',
      rewardExceptionGroups: [],
      rewardExceptionSummary: null,
      rewardExceptionWarnings: [],
      loadingRewardExceptions: false,
      rewardExceptionsError: '',
      expandedRewardExceptionGroups: [],

      topClientsOpen: false,
      topClientsMode: 'balance',
      topClientsDate: '',
      topBalances: [],
      topBalancesAsOf: '',
      topDailyRewarded: [],
      topDailyUsed: [],
      topDailyLoadedDate: '',
      topActivityView: 'rewarded',
      loadingTopBalances: false,
      loadingTopDaily: false,
      topBalancesError: '',
      topDailyError: '',

      action: 'add',
      value: '',
      reason: '',
      note: '',

      loadingClient: false,
      loadingHistory: false,
      saving: false,

      error: '',
      success: '',
    };
    this.customPeriodButton = React.createRef();
    this.topBalancesRequestId = 0;
    this.topDailyRequestId = 0;
    this.creditBalanceRequestId = 0;
  }

    componentDidMount() {
      this.loadAllClientSummary();
      this.loadCreditBalance();

      if (this.props.onReady) {
        this.props.onReady();
      }
    }

  componentWillUnmount() {
    this.topBalancesRequestId += 1;
    this.topDailyRequestId += 1;
    this.creditBalanceRequestId += 1;
  }

  getPeriodRange = (periodMode) => {
    if (periodMode === 'custom') {
      return { date_from: this.state.dateFrom, date_to: this.state.dateTo };
    }
    const dateTo = businessTodayIso();
    let dateFrom = dateTo;

    if (periodMode === 'today') {
      // Today only
    } else if (periodMode === 'last_7d') {
      dateFrom = shiftCreditReportDate(dateTo, -7);
    } else if (periodMode === 'last_90d') {
      dateFrom = shiftCreditReportDate(dateTo, -90);
    } else if (periodMode === 'this_month') {
      dateFrom = `${dateTo.slice(0, 7)}-01`;
    } else {
      dateFrom = shiftCreditReportDate(dateTo, -30);
    }

    return {
      date_from: dateFrom,
      date_to: dateTo,
    };
  };

  formatMoney = (value, showPositiveSign = false) => {
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return '—';

    const formattedValue = numberValue.toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${showPositiveSign && numberValue >= 0 ? '+' : ''}${formattedValue}`;
  };

  formatCount = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return '—';

    return numberValue.toLocaleString('ro-RO', { maximumFractionDigits: 0 });
  };

  formatDateTime = (value) => {
    return formatCreditDateTime(value);
  };

  getResponseError = (response, fallback = 'Request failed.') => {
    if (!response) return fallback;

    if (response.detail) return response.detail;
    if (response.message) return response.message;
    if (response.error) return response.error;

    if (response.status === 'ERROR' && response.message) {
      return response.message;
    }

    return fallback;
  };

  normalizeClientResponse = (response) => {
    if (!response) return null;

    let data = response.Client || response.client_data || response.clientDetails || response;

    if (typeof data !== 'object') {
      data = response;
    }

    if (!data) return null;

    const credit = data.credit ?? data.currentCredit ?? data.current_credit;

    return {
      iref: data.iref || data.client || data.client_iref || '',
      name: data.name || data.client_name || data.fullName || '',
      email: data.email || data.client_email || '',
      phone: data.phone || data.mobile || data.client_phone || '',
      credit: credit === null || credit === undefined || String(credit).trim() === ''
        ? null
        : Number(credit),
    };
  };

  getRowType = (row) => Number(row.type);

  getRowValue = (row) => Number(row.value || 0);

  isManualRow = (row) => [4, 5].includes(this.getRowType(row));

  isRewardedRow = (row) => [0, 1].includes(this.getRowType(row));

  isUsedRow = (row) => this.getRowType(row) === 2;

  getFilteredHistory = () => {
    const { history, historyFilter } = this.state;

    if (historyFilter === 'rewarded') {
      return history.filter((row) => this.isRewardedRow(row));
    }

    if (historyFilter === 'used') {
      return history.filter((row) => this.isUsedRow(row));
    }

    if (historyFilter === 'manual') {
      return history.filter((row) => this.isManualRow(row));
    }

    return history;
  };

  getHistorySummary = (rows = this.state.history) => {
    const rewarded = rows
      .filter((row) => this.isRewardedRow(row))
      .reduce((sum, row) => sum + Math.abs(this.getRowValue(row)), 0);

    const used = rows
      .filter((row) => this.isUsedRow(row))
      .reduce((sum, row) => sum + Math.abs(this.getRowValue(row)), 0);

    const net = rows.reduce((sum, row) => sum + this.getRowValue(row), 0);

    return {
      rewarded,
      used,
      net,
      transactions: rows.length,
    };
  };

  getDailySummaryRows = () => {
    if (!this.state.selectedClient) {
      return (this.state.allClientDailyRows || [])
        .slice()
        .sort((a, b) => {
          if (a.date === 'Unknown') return 1;
          if (b.date === 'Unknown') return -1;

          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
    }

    const grouped = {};

    const getDateKey = (row) => {
      const rawValue = row.datetime || row.createdOn || '';

      if (!rawValue) {
        return 'Unknown';
      }

      const value = String(rawValue).trim();

      // Backend serializer format: dd-mm-yyyy HH:mm:ss
      const europeanDateMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})/);

      if (europeanDateMatch) {
        const day = europeanDateMatch[1];
        const month = europeanDateMatch[2];
        const year = europeanDateMatch[3];

        return `${year}-${month}-${day}`;
      }

      // ISO format fallback: yyyy-mm-dd...
      const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (isoDateMatch) {
        return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}`;
      }

      return value.substring(0, 10) || 'Unknown';
    };

    this.state.history.forEach((row) => {
      const dateKey = getDateKey(row);

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          rewarded: 0,
          used: 0,
          net: 0,
          transactions: 0,
        };
      }

      const rowValue = this.getRowValue(row);

      if (this.isRewardedRow(row)) {
        grouped[dateKey].rewarded += Math.abs(rowValue);
      }

      if (this.isUsedRow(row)) {
        grouped[dateKey].used += Math.abs(rowValue);
      }

      grouped[dateKey].net += rowValue;
      grouped[dateKey].transactions += 1;
    });

    return Object.values(grouped)
      .sort((a, b) => {
        if (a.date === 'Unknown') return 1;
        if (b.date === 'Unknown') return -1;

        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  };

  getEvolutionRows = () => {
    return this.getDailySummaryRows()
      .slice(0, 14);
  };

  getClientTransactionRows = () => {
    return this.getFilteredHistory().slice(0, 20);
  };

  getHistoryQuery = (email = null, periodMode = null) => {
    const cleanEmail = String(email || '').trim().toLowerCase();
    const selectedPeriod = periodMode || this.state.periodMode;
    const range = this.getPeriodRange(selectedPeriod);

    const query = {
      date_from: range.date_from,
      date_to: range.date_to,
      orderBy_datetime: 1,
      limit: cleanEmail ? 50 : 500,
    };

    if (cleanEmail) {
      query.client_email = cleanEmail;
    }

    return query;
  };

  loadClientCredit = (email) => {
    const cleanEmail = String(email || '').trim().toLowerCase();

    if (!cleanEmail) {
      this.setState({
        error: 'Email is required.',
        selectedClient: null,
        searchEmail: '',
      });
      return null;
    }

    this.setState({
      loadingClient: true,
      error: '',
      success: '',
    });

    const response = this.state.clientApi.getData(
      Config.API_URL_CLIENT + 'getClientCreditByEmail/',
      {
        email: cleanEmail,
      }
    );

    if (!response) {
      this.setState({
        loadingClient: false,
        selectedClient: null,
        error: 'Could not load client.',
      });
      return null;
    }

    if (response.status === 'ERROR') {
      this.setState({
        loadingClient: false,
        selectedClient: null,
        error: this.getResponseError(response, 'Could not load client.'),
      });
      return null;
    }

    const selectedClient = this.normalizeClientResponse(response);

    if (!selectedClient || !selectedClient.email) {
      this.setState({
        loadingClient: false,
        selectedClient: null,
        error: 'Client not found or invalid response.',
      });
      return null;
    }

    this.setState({
      selectedClient,
      searchEmail: cleanEmail,
      loadingClient: false,
      historyFilter: 'all',
    });

    return selectedClient;
  };

  loadAllClientSummary = (periodMode = null) => {
    const selectedPeriod = periodMode || this.state.periodMode;
    const range = this.getPeriodRange(selectedPeriod);

    this.setState({
      loadingHistory: true,
      error: '',
    });

    const response = this.state.clientApi.getData(
      Config.API_URL_CLIENT_CREDIT + 'dailySummary/',
      {
        date_from: range.date_from,
        date_to: range.date_to,
      }
    );

    if (!response) {
      this.setState({
        loadingHistory: false,
        history: [],
        allClientSummary: {
          rewarded: 0,
          used: 0,
          net: 0,
          transactions: 0,
        },
        allClientDailyRows: [],
        error: 'Could not load credit summary.',
      });
      return;
    }

    if (response.status === 'ERROR') {
      this.setState({
        loadingHistory: false,
        history: [],
        allClientSummary: {
          rewarded: 0,
          used: 0,
          net: 0,
          transactions: 0,
        },
        allClientDailyRows: [],
        error: this.getResponseError(response, 'Could not load credit summary.'),
      });
      return;
    }

    this.setState({
      loadingHistory: false,
      history: [],
      allClientSummary: response.summary || {
        rewarded: 0,
        used: 0,
        net: 0,
        transactions: 0,
      },
      allClientDailyRows: response.daily_rows || [],
    });
  };

  loadCreditBalance = () => {
    const periodEnd = this.getPeriodRange(this.state.periodMode).date_to;
    const today = businessTodayIso();
    const date = periodEnd > today ? today : periodEnd;
    const requestId = ++this.creditBalanceRequestId;

    this.setState({
      creditBalanceAmount: null,
      creditBalanceDate: date,
      creditBalanceEstimated: date < today,
      loadingCreditBalance: true,
      creditBalanceError: '',
    });

    this.state.clientApi.getDataAsync(
      Config.API_URL_CLIENT_CREDIT + 'balanceAsOf/',
      { date },
    ).then(
      (response) => {
        if (requestId !== this.creditBalanceRequestId) return;
        if (!response || response.status !== 'OK' || response.date !== date
          || !Number.isFinite(response.amount)) {
          this.setState({
            loadingCreditBalance: false,
            creditBalanceError: this.getResponseError(response, 'Could not load client credit balance.'),
          });
          return;
        }
        this.setState({
          creditBalanceAmount: response.amount,
          creditBalanceEstimated: Boolean(response.estimated),
          loadingCreditBalance: false,
        });
      },
      (xhr) => {
        if (requestId !== this.creditBalanceRequestId) return;
        this.setState({
          loadingCreditBalance: false,
          creditBalanceError: this.getResponseError(xhr && xhr.responseJSON, 'Could not load client credit balance.'),
        });
      },
    );
  };

  loadCreditHistory = (email = null, periodMode = null) => {
    const cleanEmail = String(email || '').trim().toLowerCase();
    const query = this.getHistoryQuery(cleanEmail, periodMode);

    this.setState({
      loadingHistory: true,
      error: '',
    });

    const response = this.state.clientApi.getData(
      Config.API_URL_CLIENT_CREDIT,
      query
    );

    if (!response) {
      this.setState({
        loadingHistory: false,
        history: [],
        error: 'Could not load credit history.',
      });
      return;
    }

    if (response.status === 'ERROR') {
      this.setState({
        loadingHistory: false,
        history: [],
        error: this.getResponseError(response, 'Could not load credit history.'),
      });
      return;
    }

    this.setState({
      loadingHistory: false,
      history: response.ClientCreditHistory || response.results || response.history || [],
    });
  };

  openRewardExceptions = (date) => {
    const selectedDate = String(date || '').trim();
    if (shiftCreditReportDate(selectedDate, 0) !== selectedDate) return;

    this.setState({
      rewardExceptionsOpen: true,
      rewardExceptionsDate: selectedDate,
      rewardExceptionGroups: [],
      rewardExceptionSummary: null,
      rewardExceptionWarnings: [],
      loadingRewardExceptions: true,
      rewardExceptionsError: '',
      expandedRewardExceptionGroups: [],
    });

    this.state.clientApi.getDataAsync(
      Config.API_URL_CLIENT_CREDIT + 'rewardExceptions/',
      {
        date: selectedDate,
        include_inferred: 1,
        limit: 500,
      }
    ).then(
      (response) => {
        if (this.state.rewardExceptionsDate !== selectedDate) return;

        if (!response || response.status === 'ERROR') {
          this.setState({
            loadingRewardExceptions: false,
            rewardExceptionsError: this.getResponseError(
              response,
              'Could not load reward exceptions.'
            ),
          });
          return;
        }

        this.setState({
          loadingRewardExceptions: false,
          rewardExceptionGroups: response.groups || [],
          rewardExceptionSummary: response.summary || null,
          rewardExceptionWarnings: response.warnings || [],
        });
      },
      (xhr) => {
        if (this.state.rewardExceptionsDate !== selectedDate) return;
        this.setState({
          loadingRewardExceptions: false,
          rewardExceptionsError: this.getResponseError(
            xhr && xhr.responseJSON,
            'Could not load reward exceptions.'
          ),
        });
      }
    );
  };

  closeRewardExceptions = () => {
    this.setState({
      rewardExceptionsOpen: false,
    });
  };

  navigateRewardExceptions = (dayOffset) => {
    if (this.state.loadingRewardExceptions) return;

    const selectedDate = shiftCreditReportDate(
      this.state.rewardExceptionsDate,
      dayOffset
    );
    if (selectedDate) this.openRewardExceptions(selectedDate);
  };

  toggleRewardExceptionGroup = (groupKey) => {
    this.setState((state) => {
      const expandedGroups = state.expandedRewardExceptionGroups || [];
      return {
        expandedRewardExceptionGroups: expandedGroups.includes(groupKey)
          ? expandedGroups.filter((key) => key !== groupKey)
          : [...expandedGroups, groupKey],
      };
    });
  };

  openTopClients = () => {
    if (this.state.topClientsOpen) return;
    const periodEnd = this.getPeriodRange(this.state.periodMode).date_to;
    const today = businessTodayIso();
    const selectedDate = periodEnd && periodEnd <= today ? periodEnd : today;

    this.setState({
      topClientsOpen: true,
      topClientsMode: 'balance',
      topClientsDate: selectedDate,
      topBalances: [],
      topBalancesAsOf: '',
      topDailyRewarded: [],
      topDailyUsed: [],
      topDailyLoadedDate: '',
      topActivityView: 'rewarded',
      loadingTopBalances: false,
      loadingTopDaily: false,
      topBalancesError: '',
      topDailyError: '',
    }, this.loadTopBalances);
  };

  closeTopClients = () => {
    this.topBalancesRequestId += 1;
    this.topDailyRequestId += 1;
    this.setState({ topClientsOpen: false });
  };

  setTopClientsMode = (mode) => {
    if (!mode || mode === this.state.topClientsMode) return;
    const shouldLoadDay = mode === 'daily' && this.state.topDailyLoadedDate !== this.state.topClientsDate
      && !this.state.loadingTopDaily && !this.state.topDailyError;
    this.setState({ topClientsMode: mode, loadingTopDaily: shouldLoadDay || this.state.loadingTopDaily }, () => {
      if (shouldLoadDay) this.loadTopDaily(this.state.topClientsDate);
    });
  };

  loadTopBalances = () => {
    const requestId = ++this.topBalancesRequestId;
    this.setState({ loadingTopBalances: true, topBalancesError: '' });

    this.state.clientApi.getDataAsync(
      Config.API_URL_CLIENT_CREDIT + 'topClients/',
      { kind: 'balance' }
    ).then(
      (response) => {
        if (requestId !== this.topBalancesRequestId || !this.state.topClientsOpen) return;
        if (!response || response.status !== 'OK' || !Array.isArray(response.rows)) {
          this.setState({
            loadingTopBalances: false,
            topBalancesError: this.getResponseError(response, 'Could not load current balances.'),
          });
          return;
        }
        this.setState({
          loadingTopBalances: false,
          topBalances: response.rows,
          topBalancesAsOf: response.as_of || '',
        });
      },
      (xhr) => {
        if (requestId !== this.topBalancesRequestId || !this.state.topClientsOpen) return;
        this.setState({
          loadingTopBalances: false,
          topBalancesError: this.getResponseError(xhr && xhr.responseJSON, 'Could not load current balances.'),
        });
      }
    );
  };

  loadTopDaily = (date) => {
    const requestId = ++this.topDailyRequestId;
    this.setState({ loadingTopDaily: true, topDailyError: '' });

    this.state.clientApi.getDataAsync(
      Config.API_URL_CLIENT_CREDIT + 'topClients/',
      { kind: 'daily', date }
    ).then(
      (response) => {
        if (requestId !== this.topDailyRequestId || !this.state.topClientsOpen || this.state.topClientsDate !== date) return;
        if (!response || response.status !== 'OK' || response.date !== date
          || !Array.isArray(response.rewarded) || !Array.isArray(response.used)) {
          this.setState({
            loadingTopDaily: false,
            topDailyRewarded: [],
            topDailyUsed: [],
            topDailyLoadedDate: '',
            topDailyError: this.getResponseError(response, 'Could not load daily activity.'),
          });
          return;
        }
        this.setState({
          loadingTopDaily: false,
          topDailyRewarded: response.rewarded,
          topDailyUsed: response.used,
          topDailyLoadedDate: date,
        });
      },
      (xhr) => {
        if (requestId !== this.topDailyRequestId || !this.state.topClientsOpen || this.state.topClientsDate !== date) return;
        this.setState({
          loadingTopDaily: false,
          topDailyRewarded: [],
          topDailyUsed: [],
          topDailyLoadedDate: '',
          topDailyError: this.getResponseError(xhr && xhr.responseJSON, 'Could not load daily activity.'),
        });
      }
    );
  };

  selectTopClientsDate = (date) => {
    if (shiftCreditReportDate(date, 0) !== date || date > businessTodayIso()
      || date === this.state.topClientsDate) return;
    this.setState({
      topClientsDate: date,
      topDailyRewarded: [],
      topDailyUsed: [],
      topDailyLoadedDate: '',
      loadingTopDaily: true,
      topDailyError: '',
    }, () => this.loadTopDaily(date));
  };

  navigateTopClientsDate = (dayOffset) => {
    const date = shiftCreditReportDate(this.state.topClientsDate, dayOffset);
    if (date) this.selectTopClientsDate(date);
  };

  handleSearchClient = () => {
    const { email, periodMode } = this.state;

    const selectedClient = this.loadClientCredit(email);

    if (selectedClient) {
      this.loadCreditHistory(selectedClient.email || email, periodMode);
    }
  };

  handleClearClient = () => {
    this.setState({
      email: '',
      searchEmail: '',
      selectedClient: null,
      value: '',
      reason: '',
      note: '',
      action: 'add',
      success: '',
      error: '',
      historyFilter: 'all',
      history: [],
    }, () => {
      this.loadAllClientSummary(this.state.periodMode);
    });
  };

  handlePeriodChange = (event, newPeriodMode) => {
    if (newPeriodMode === 'custom') {
      const range = this.getPeriodRange(this.state.periodMode);
      this.setState({
        customPeriodOpen: true,
        draftDateFrom: this.state.dateFrom || range.date_from,
        draftDateTo: this.state.dateTo || range.date_to,
        customPeriodError: '',
      });
      return;
    }
    if (!newPeriodMode || newPeriodMode === this.state.periodMode) return;

    this.setState({
      periodMode: newPeriodMode,
    }, this.reloadPeriod);
  };

  reloadPeriod = () => {
    this.loadCreditBalance();
    if (this.state.selectedClient || this.state.searchEmail) {
      this.loadCreditHistory(this.state.searchEmail, this.state.periodMode);
    } else {
      this.loadAllClientSummary(this.state.periodMode);
    }
  };

  handleCustomPeriodApply = () => {
    const { draftDateFrom, draftDateTo } = this.state;
    const customPeriodError = validateCustomPeriod(draftDateFrom, draftDateTo, MAX_CREDIT_PERIOD_DAYS);
    if (customPeriodError) {
      this.setState({ customPeriodError });
      return;
    }
    if (this.state.periodMode === 'custom' && this.state.dateFrom === draftDateFrom && this.state.dateTo === draftDateTo) {
      this.setState({ customPeriodOpen: false });
      return;
    }
    this.setState({
      periodMode: 'custom',
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      customPeriodOpen: false,
    }, this.reloadPeriod);
  };

  handleAdjustCredit = () => {
    const {
      selectedClient,
      searchEmail,
      action,
      value,
      reason,
      note,
    } = this.state;

    if (!selectedClient || !searchEmail) {
      this.setState({
        error: 'Search and select a client first.',
        success: '',
      });
      return;
    }

    const numericValue = Number(value);

    if (!numericValue || numericValue <= 0) {
      this.setState({
        error: 'Amount must be greater than 0.',
        success: '',
      });
      return;
    }

    if (!String(reason || '').trim()) {
      this.setState({
        error: 'Reason is required.',
        success: '',
      });
      return;
    }

    if (!String(note || '').trim()) {
      this.setState({
        error: 'Internal note is required.',
        success: '',
      });
      return;
    }

    if (action === 'remove' && numericValue > Number(selectedClient.credit || 0)) {
      this.setState({
        error: 'Cannot remove more credit than the client currently has.',
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    const response = this.state.clientApi.pushData(
      Config.API_URL_CLIENT + 'adjustClientCredit/',
      {
        email: searchEmail,
        action,
        value: numericValue,
        reason,
        note: `${reason} - ${String(note || '').trim()}`,
      }
    );

    if (!response) {
      this.setState({
        saving: false,
        error: 'Could not update client credit.',
      });
      return;
    }

    if (response.status === 'ERROR') {
      this.setState({
        saving: false,
        error: this.getResponseError(response, 'Could not update client credit.'),
      });
      return;
    }

    this.loadClientCredit(searchEmail);
    this.loadCreditHistory(searchEmail);
    this.loadCreditBalance();

    this.setState({
      saving: false,
      value: '',
      reason: '',
      note: '',
      success: 'Client credit updated successfully.',
    });
  };

  renderCompactMetric = (title, value, subtitle, tone = 'default', info = null) => {
    const toneSx = {
      default: '#020617',
      positive: '#166534',
      negative: '#991b1b',
      neutral: '#1e3a8a',
    };

    return (
      <Box
        sx={{
          minWidth: 0,
          px: { xs: 1, sm: 1.25 },
          py: { xs: 0.75, sm: 0.9 },
          borderRadius: '14px',
          bgcolor: '#f8fafc',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          height: '100%',
          textAlign: 'center',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center" sx={{ position: 'relative' }}>
          <Typography
            sx={{
              color: '#64748b',
              fontSize: { xs: '0.76rem', sm: '0.8rem' },
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            {title}
          </Typography>
          {info ? (
            <Tooltip title={info} describeChild>
              <IconButton size="small" aria-label={`About ${title.toLowerCase()}`} sx={{ p: 0, color: '#64748b', position: 'absolute', right: 0 }}>
                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>

        <Typography
          sx={{
            color: toneSx[tone] || toneSx.default,
            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.12rem' },
            fontWeight: 950,
            lineHeight: 1.15,
            mt: 0.25,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>

        {subtitle ? (
          <Typography
            sx={{
              color: '#94a3b8',
              fontSize: { xs: '0.6rem', sm: '0.65rem' },
              lineHeight: 1.15,
              mt: 0.25,
              whiteSpace: info ? 'nowrap' : 'normal',
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    );
  };

  renderPanelHeader = (title, subtitle, action = null) => (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5, md: 1.75 },
        pb: 1.1,
        flexShrink: 0,
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box
          sx={{
            minWidth: 0,
            textAlign: 'left',
          }}
        >
          <Typography
            sx={{
              fontWeight: 950,
              color: '#020617',
              textAlign: 'left',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.76rem',
              textAlign: 'left',
              lineHeight: 1.35,
              mt: 0.25,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        {action ? (
          <Box
            sx={{
              flexShrink: 0,
              pt: 0,
            }}
          >
            {action}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );

  renderEvolutionChart = () => {
    const rows = this.getEvolutionRows();
    const maxValue = Math.max(
      1,
      ...rows.map((row) => Math.max(row.rewarded, row.used, Math.abs(row.net)))
    );

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Credit evolution',
          'Rewarded versus used credit',
          <Button
            size="small"
            variant="text"
            onClick={this.openTopClients}
            aria-haspopup="dialog"
            aria-controls={this.state.topClientsOpen ? 'credit-top-clients' : undefined}
            sx={{ color: '#1d4ed8', fontWeight: 850, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Top clients
          </Button>
        )}

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {rows.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed rgba(100, 116, 139, 0.3)',
                borderRadius: '14px',
                bgcolor: '#f8fafc',
              }}
            >
              <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
                No credit movements found for this period.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.85} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
              {rows.map((row) => {
                const rewardedWidth = Math.max(2, (row.rewarded / maxValue) * 100);
                const usedWidth = Math.max(2, (row.used / maxValue) * 100);

                return (
                  <Box key={row.date}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.35 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 800 }}>
                        {row.date}
                      </Typography>

                      <Typography sx={{ fontSize: '0.72rem', color: '#334155', fontWeight: 800 }}>
                        Net {this.formatMoney(row.net, true)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.35}>
                      <Box sx={{ height: 8, bgcolor: '#ecfdf5', borderRadius: 99, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${rewardedWidth}%`,
                            height: '100%',
                            bgcolor: '#22c55e',
                            borderRadius: 99,
                          }}
                        />
                      </Box>

                      <Box sx={{ height: 8, bgcolor: '#fef2f2', borderRadius: 99, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${usedWidth}%`,
                            height: '100%',
                            bgcolor: '#ef4444',
                            borderRadius: 99,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Stack
          component="footer"
          aria-label="Credit evolution legend"
          direction="row"
          spacing={0.75}
          sx={{
            px: { xs: 1.25, sm: 1.5, md: 1.75 },
            py: 1,
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#ffffff',
          }}
        >
          <Chip
            size="small"
            label="Rewarded"
            sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 800 }}
          />
          <Chip
            size="small"
            label="Used"
            sx={{ bgcolor: '#fef2f2', color: '#991b1b', fontWeight: 800 }}
          />
        </Stack>
      </Paper>
    );
  };

  renderClientPanel = (clientSummary) => {
    const {
      email,
      selectedClient,
      action,
      value,
      reason,
      note,
      loadingClient,
      saving,
    } = this.state;

    const numericValue = Number(value || 0);
    const signedAdjustment = action === 'remove' ? -numericValue : numericValue;
    const newBalance = selectedClient && selectedClient.credit !== null
      ? Number(selectedClient.credit) + signedAdjustment
      : null;

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Client management',
          'Search client and adjust credit',
          selectedClient ? (
            <Button
              size="small"
              variant="text"
              onClick={this.handleClearClient}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                color: '#475569',
                flexShrink: 0,
              }}
            >
              Clear
            </Button>
          ) : null
        )}

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
            <TextField
              fullWidth
              size="small"
              label="Client email"
              value={email}
              onChange={(event) => this.setState({ email: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  this.handleSearchClient();
                }
              }}
            />

            <Button
              variant="contained"
              onClick={this.handleSearchClient}
              disabled={loadingClient}
              sx={{
                minWidth: { xs: '100%', sm: 110 },
                bgcolor: '#0f172a',
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: '10px',

                '&:hover': {
                  bgcolor: '#111827',
                },
              }}
            >
              {loadingClient ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
          </Stack>

          {!selectedClient ? (
            <Box
              sx={{
                p: 1.35,
                borderRadius: '14px',
                bgcolor: '#f8fafc',
                border: '1px dashed rgba(100, 116, 139, 0.3)',
                mb: 1.25,
              }}
            >
              <Typography sx={{ fontWeight: 850, color: '#334155', mb: 0.35 }}>
                No client selected
              </Typography>

              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.76rem' }}>
                The report shows all clients. Search by email to manage one client.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                mb: 1.25,
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950, color: '#020617', lineHeight: 1.15 }}>
                    {selectedClient.name || 'Client'}
                  </Typography>

                  <Typography sx={{ color: '#64748b', fontSize: '0.74rem', wordBreak: 'break-word' }}>
                    {selectedClient.email || '-'}
                  </Typography>
                </Box>

                <Chip
                  label="Selected"
                  size="small"
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#1e3a8a',
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                />
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1}>
                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 800 }}>
                    Current balance
                  </Typography>

                  <Typography
                    sx={{
                      color: Number(selectedClient.credit || 0) > 0 ? '#166534' : '#020617',
                      fontSize: { xs: '1.55rem', md: '1.75rem' },
                      fontWeight: 950,
                      lineHeight: 1.05,
                    }}
                  >
                    {this.formatMoney(selectedClient.credit)}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    {PERIOD_LABELS[this.state.periodMode]}
                  </Typography>

                  <Typography sx={{ color: '#166534', fontSize: '0.75rem', fontWeight: 900 }}>
                    Rewarded {this.formatMoney(clientSummary.rewarded)}
                  </Typography>

                  <Typography sx={{ color: '#991b1b', fontSize: '0.75rem', fontWeight: 900 }}>
                    Used {this.formatMoney(clientSummary.used)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          <Typography sx={{ fontWeight: 950, color: '#020617', mb: 1 }}>
            Manual adjustment
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Type"
                value={action}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ action: event.target.value })}
              >
                <MenuItem value="add">Add credit</MenuItem>
                <MenuItem value="remove">Remove credit</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Amount"
                value={value}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ value: event.target.value })}
                inputProps={{
                  min: 0,
                  step: '0.01',
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Reason"
                value={reason}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ reason: event.target.value })}
              >
                {ADJUSTMENT_REASONS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Internal note"
                value={note}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ note: event.target.value })}
                inputProps={{
                  maxLength: 255,
                }}
                helperText={`${String(note || '').length}/255`}
              />
            </Grid>
          </Grid>

          {selectedClient && numericValue > 0 ? (
            <Box
              sx={{
                mt: 1,
                p: 1.15,
                borderRadius: '14px',
                bgcolor: action === 'remove' ? '#fffbeb' : '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 800 }}>
                  Current balance
                </Typography>

                <Typography sx={{ color: '#334155', fontSize: '0.75rem', fontWeight: 900 }}>
                  {this.formatMoney(selectedClient.credit)}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 800 }}>
                  Adjustment
                </Typography>

                <Typography
                  sx={{
                    color: action === 'remove' ? '#991b1b' : '#166534',
                    fontSize: '0.75rem',
                    fontWeight: 950,
                  }}
                >
                  {this.formatMoney(signedAdjustment, true)}
                </Typography>
              </Stack>

              <Divider sx={{ my: 0.6 }} />

              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#020617', fontSize: '0.8rem', fontWeight: 950 }}>
                  New balance
                </Typography>

                <Typography
                  sx={{
                    color: newBalance >= 0 ? '#020617' : '#991b1b',
                    fontSize: '0.8rem',
                    fontWeight: 950,
                  }}
                >
                  {this.formatMoney(newBalance)}
                </Typography>
              </Stack>
            </Box>
          ) : null}

          {action === 'remove' && selectedClient ? (
            <Alert severity="warning" sx={{ mt: 1, borderRadius: '12px', fontSize: '0.78rem' }}>
              Removing credit reduces the client&apos;s available balance.
            </Alert>
          ) : null}
        </Box>

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            pt: 1,
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#ffffff',
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedClient || saving}
            onClick={this.handleAdjustCredit}
            sx={{
              height: 42,
              bgcolor: action === 'remove' ? '#991b1b' : '#0f172a',
              textTransform: 'none',
              fontWeight: 950,
              borderRadius: '12px',

              '&:hover': {
                bgcolor: action === 'remove' ? '#7f1d1d' : '#111827',
              },
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : action === 'remove' ? (
              'Confirm remove credit'
            ) : (
              'Confirm add credit'
            )}
          </Button>
        </Box>
      </Paper>
    );
  };

  renderTopClientRows = (rows, emptyMessage, tableLabel, amountLabel) => (
    <TableContainer sx={{ border: '1px solid rgba(15, 23, 42, 0.06)', borderRadius: '12px' }}>
      <Table size="small" aria-label={tableLabel}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '1%', px: 0.75, textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 950, bgcolor: '#f8fafc' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Client</TableCell>
            <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>{amountLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} sx={{ py: 3, color: '#64748b', textAlign: 'center' }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : rows.map((row, index) => (
            <TableRow key={row.client}>
              <TableCell sx={{ px: 0.75, textAlign: 'center', color: '#64748b', fontWeight: 850 }}>{index + 1}</TableCell>
              <TableCell sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#020617', fontSize: '0.82rem', fontWeight: 850 }}>
                  {row.client_name || `Client #${row.client}`}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', overflowWrap: 'anywhere' }}>
                  {row.client_email || '—'}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ color: '#166534', fontWeight: 950, whiteSpace: 'nowrap' }}>
                {this.formatMoney(row.amount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  renderTopClientsModal = () => {
    const {
      topClientsOpen,
      topClientsMode,
      topClientsDate,
      topBalances,
      topBalancesAsOf,
      topDailyRewarded,
      topDailyUsed,
      topActivityView,
      loadingTopBalances,
      loadingTopDaily,
      topBalancesError,
      topDailyError,
    } = this.state;
    const today = businessTodayIso();
    const showingBalances = topClientsMode === 'balance';
    const activityRows = topActivityView === 'used' ? topDailyUsed : topDailyRewarded;
    const loading = showingBalances ? loadingTopBalances : loadingTopDaily;
    const error = showingBalances ? topBalancesError : topDailyError;

    return (
      <Dialog
        id="credit-top-clients"
        open={topClientsOpen}
        onClose={this.closeTopClients}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="credit-top-clients-title"
        PaperProps={{ sx: { height: 600, maxHeight: 'calc(100vh - 16px)', m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Typography id="credit-top-clients-title" sx={{ color: '#020617', fontSize: '1.05rem', fontWeight: 950 }}>
                Top 5 clients
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.25 }}>
                Client accounts · excludes erased profiles
              </Typography>
            </Box>
            <Tooltip title="Close">
              <IconButton size="small" onClick={this.closeTopClients} aria-label="Close top clients" sx={{ mt: -0.5 }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={topClientsMode}
              onChange={(event, value) => this.setTopClientsMode(value)}
              aria-label="Top clients view"
              sx={{ alignSelf: 'flex-start', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 850 } }}
            >
              <ToggleButton value="balance">Current balance</ToggleButton>
              <ToggleButton value="daily">By day</ToggleButton>
            </ToggleButtonGroup>

            {showingBalances ? (
              <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                Highest available credit now{topBalancesAsOf ? ` · ${this.formatDateTime(topBalancesAsOf)}` : ''}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 1, rowGap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <Tabs
                    value={topActivityView}
                    onChange={(event, value) => this.setState({ topActivityView: value })}
                    aria-label="Top clients activity type"
                    sx={{
                      minHeight: 38,
                      '& .MuiTabs-indicator': { height: 2, bgcolor: '#0f172a' },
                      '& .MuiTab-root': { minHeight: 38, minWidth: 76, px: 1.25, textTransform: 'none', fontWeight: 800, fontSize: '0.82rem' },
                      '& .MuiTab-root.Mui-selected': { color: '#0f172a' },
                    }}
                  >
                    <Tab value="rewarded" label="Rewarded" />
                    <Tab value="used" label="Used" />
                  </Tabs>
                  <Tooltip describeChild title="Rewarded includes Reward and Bonus. Used is net of credit use reversals.">
                    <IconButton size="small" aria-label="About rewarded and used credit" sx={{ color: '#64748b' }}>
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <Tooltip title="Previous day">
                    <IconButton size="small" onClick={() => this.navigateTopClientsDate(-1)} aria-label="Previous top clients day"><ChevronLeftRoundedIcon /></IconButton>
                  </Tooltip>
                  <TextField
                    size="small"
                    type="date"
                    label="Activity date"
                    value={topClientsDate}
                    onChange={(event) => this.selectTopClientsDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: today }}
                    sx={{ width: 170, '& input': { fontSize: { xs: '16px', sm: '0.8rem' } } }}
                  />
                  <Tooltip title="Next day">
                    <span><IconButton size="small" disabled={topClientsDate >= today} onClick={() => this.navigateTopClientsDate(1)} aria-label="Next top clients day"><ChevronRightRoundedIcon /></IconButton></span>
                  </Tooltip>
                </Stack>
              </Box>
            )}

            <Box sx={{ minHeight: 290 }}>
              {loading ? (
                <Stack alignItems="center" justifyContent="center" role="status" aria-label="Loading top clients" sx={{ minHeight: 290 }}>
                  <CircularProgress size={24} />
                </Stack>
              ) : error ? (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={showingBalances ? this.loadTopBalances : () => this.loadTopDaily(topClientsDate)}>Retry</Button>}>
                  {error}
                </Alert>
              ) : showingBalances ? (
                this.renderTopClientRows(topBalances, 'No clients have a positive credit balance.', 'Top current balances', 'Balance')
              ) : (
                this.renderTopClientRows(
                  activityRows,
                  `No ${topActivityView} credit on ${topClientsDate}.`,
                  `Top ${topActivityView} clients on ${topClientsDate}`,
                  'Amount'
                )
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

  renderRewardExceptionsModal = () => {
    const {
      rewardExceptionsOpen,
      rewardExceptionsDate,
      rewardExceptionGroups,
      rewardExceptionSummary,
      rewardExceptionWarnings,
      loadingRewardExceptions,
      rewardExceptionsError,
      expandedRewardExceptionGroups,
    } = this.state;
    const summary = rewardExceptionSummary || {
      clients: 0,
      orders: 0,
      logged: 0,
      inferred: 0,
      expected_reward: 0,
      truncated: false,
    };

    return (
      <Dialog
        open={rewardExceptionsOpen}
        onClose={this.closeRewardExceptions}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        aria-labelledby="reward-exceptions-title"
        PaperProps={{
          sx: {
            height: {
              xs: 'calc(100vh - 16px)',
              sm: '88vh',
              md: '85vh',
            },
            maxHeight: {
              xs: 'calc(100vh - 16px)',
              sm: '88vh',
              md: '85vh',
            },
            m: { xs: 1, sm: 2 },
            width: {
              xs: 'calc(100% - 16px)',
              sm: 'calc(100% - 32px)',
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pr: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Typography
                id="reward-exceptions-title"
                sx={{ color: '#020617', fontSize: '1.05rem', fontWeight: 950 }}
              >
                Credit reward exceptions
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.25 }}>
                Grouped by client email
              </Typography>
            </Box>

            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={this.closeRewardExceptions}
                aria-label="Close reward exceptions"
                sx={{ mt: -0.5 }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 1 }}>
            <Tooltip title="Previous date">
              <span>
                <IconButton
                  size="small"
                  disabled={loadingRewardExceptions}
                  onClick={() => this.navigateRewardExceptions(-1)}
                  aria-label="Previous reward exception date"
                >
                  <ChevronLeftRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Typography
              aria-live="polite"
              sx={{
                color: '#0f172a',
                fontSize: '0.9rem',
                fontWeight: 900,
                minWidth: 96,
                textAlign: 'center',
              }}
            >
              {rewardExceptionsDate}
            </Typography>

            <Tooltip title="Next date">
              <span>
                <IconButton
                  size="small"
                  disabled={loadingRewardExceptions}
                  onClick={() => this.navigateRewardExceptions(1)}
                  aria-label="Next reward exception date"
                >
                  <ChevronRightRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: '#f8fafc',
            flex: 1,
            minHeight: 0,
            p: { xs: 1, sm: 2 },
          }}
        >
          {loadingRewardExceptions ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
              <CircularProgress size={28} />
              <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                Loading reward exceptions…
              </Typography>
            </Stack>
          ) : null}

          {!loadingRewardExceptions && rewardExceptionsError ? (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {rewardExceptionsError}
            </Alert>
          ) : null}

          {!loadingRewardExceptions && !rewardExceptionsError ? (
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                <Chip size="small" label={`${this.formatCount(summary.orders || 0)} orders`} />
                <Chip size="small" label={`${this.formatCount(summary.clients || 0)} clients`} />
                <Stack direction="row" spacing={0.15} alignItems="center">
                  <Chip size="small" color="warning" label={`Logged: ${this.formatCount(summary.logged || 0)}`} />
                  <Tooltip
                    arrow
                    describeChild
                    enterTouchDelay={0}
                    leaveTouchDelay={6000}
                    title={(
                      <Typography component="span" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                        Recorded when the automatic credit reward was skipped.
                      </Typography>
                    )}
                  >
                    <IconButton
                      size="small"
                      aria-label="About logged reward exceptions"
                      sx={{ width: 28, height: 28, color: '#92400e' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.15} alignItems="center">
                  <Chip size="small" label={`Detected: ${this.formatCount(summary.inferred || 0)}`} />
                  <Tooltip
                    arrow
                    describeChild
                    enterTouchDelay={0}
                    leaveTouchDelay={6000}
                    title={(
                      <Typography component="span" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                        Completed, eligible orders with no automatic reward history. The report
                        reconstructs the exception from the order; because the original reason was
                        not recorded, it groups the order by creation date.
                      </Typography>
                    )}
                  >
                    <IconButton
                      size="small"
                      aria-label="About detected reward exceptions"
                      sx={{ width: 28, height: 28, color: '#475569' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Chip
                  size="small"
                  color="success"
                  label={`Expected credit ${this.formatMoney(summary.expected_reward)}`}
                />
              </Stack>

              {(rewardExceptionWarnings || []).map((warning) => (
                <Alert key={warning} severity="warning" sx={{ borderRadius: '12px' }}>
                  {warning}
                </Alert>
              ))}

              {summary.truncated ? (
                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                  The result reached the safety limit. Narrow the report before reconciliation.
                </Alert>
              ) : null}

              {rewardExceptionGroups.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '12px' }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
                    No reward exceptions were found for this date.
                  </Typography>
                </Paper>
              ) : null}

              {rewardExceptionGroups.map((group, groupIndex) => {
                const orderCount = Number(
                  group.orders_count || (group.orders || []).length
                );
                const isExpanded = (expandedRewardExceptionGroups || []).includes(
                  group.group_key
                );
                const detailsId = `reward-exception-orders-${groupIndex}`;
                const orderTotalUnknownCount = Number(
                  group.order_total_unknown_count || 0
                );
                const expectedUnknownCount = Number(
                  group.expected_reward_unknown_count || 0
                );
                const unavailableLabels = [
                  orderTotalUnknownCount
                    ? `${this.formatCount(orderTotalUnknownCount)} order total${orderTotalUnknownCount === 1 ? '' : 's'} unavailable`
                    : '',
                  expectedUnknownCount
                    ? `${this.formatCount(expectedUnknownCount)} expected credit value${expectedUnknownCount === 1 ? '' : 's'} unavailable`
                    : '',
                ].filter(Boolean).join(' · ');

                return (
                  <Paper
                    key={group.group_key}
                    elevation={0}
                    sx={{
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      bgcolor: '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.25,
                        borderBottom: isExpanded
                          ? '1px solid rgba(15, 23, 42, 0.06)'
                          : 0,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={0.75}
                      >
                        <Box>
                          <Typography sx={{ color: '#0f172a', fontSize: '0.9rem', fontWeight: 950 }}>
                            {group.client_email || `Client ${group.client || '-'}`}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                            Client {group.client || '-'} · {this.formatCount(orderCount)} {orderCount === 1 ? 'order' : 'orders'}
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                          <Stack
                            direction="row"
                            spacing={1.25}
                            sx={{ flexWrap: 'wrap', rowGap: 0.25 }}
                          >
                            <Typography sx={{ color: '#475569', fontSize: '0.82rem', fontWeight: 850, whiteSpace: 'nowrap' }}>
                              Order total {this.formatMoney(group.order_total)}
                            </Typography>
                            <Typography sx={{ color: '#92400e', fontSize: '0.9rem', fontWeight: 950, whiteSpace: 'nowrap' }}>
                              Expected credit {this.formatMoney(group.expected_reward)}
                            </Typography>
                          </Stack>
                          {unavailableLabels ? (
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', mt: 0.2 }}>
                              {unavailableLabels}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.6, mt: 0.8 }}>
                        {(group.reasons || []).map((reason) => (
                          <Chip
                            key={reason.code}
                            size="small"
                            color="warning"
                            label={`${reason.label} (${this.formatCount(reason.count)})`}
                          />
                        ))}
                      </Stack>

                      <Button
                        type="button"
                        size="small"
                        onClick={() => this.toggleRewardExceptionGroup(group.group_key)}
                        aria-expanded={isExpanded}
                        aria-controls={detailsId}
                        endIcon={(
                          <ExpandMoreRoundedIcon
                            sx={{
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 160ms ease',
                            }}
                          />
                        )}
                        sx={{
                          mt: 0.75,
                          px: 0.5,
                          color: '#334155',
                          fontSize: '0.75rem',
                          fontWeight: 850,
                          textTransform: 'none',
                        }}
                      >
                        {isExpanded
                          ? 'Hide orders'
                          : `Show ${this.formatCount(orderCount)} ${orderCount === 1 ? 'order' : 'orders'}`}
                      </Button>
                    </Box>

                    {isExpanded ? (
                      <TableContainer
                        id={detailsId}
                        sx={{
                          maxHeight: { xs: 'none', sm: 280 },
                          overflow: { xs: 'visible', sm: 'auto' },
                        }}
                      >
                        <Table
                          stickyHeader
                          size="small"
                          aria-label={`Reward exceptions for ${group.client_email || group.client}`}
                          sx={{
                            display: { xs: 'block', sm: 'table' },
                            '& .MuiTableHead-root': {
                              display: { xs: 'none', sm: 'table-header-group' },
                            },
                            '& .MuiTableBody-root': {
                              display: { xs: 'grid', sm: 'table-row-group' },
                              gap: { xs: 1, sm: 0 },
                              p: { xs: 1, sm: 0 },
                            },
                            '& .MuiTableRow-root': {
                              display: { xs: 'grid', sm: 'table-row' },
                              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))' },
                              columnGap: { xs: 1.25, sm: 0 },
                              rowGap: { xs: 0.25, sm: 0 },
                              border: { xs: '1px solid rgba(15, 23, 42, 0.08)', sm: 0 },
                              borderRadius: { xs: '10px', sm: 0 },
                              p: { xs: 0.7, sm: 0 },
                            },
                            '& .MuiTableCell-root': {
                              display: { xs: 'block', sm: 'table-cell' },
                              borderBottom: { xs: 0, sm: '1px solid rgba(224, 224, 224, 1)' },
                              p: { xs: 0.45, sm: '6px 16px' },
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}>Order</TableCell>
                              <TableCell sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}>Location</TableCell>
                              <TableCell sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}>Decision</TableCell>
                              <TableCell sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}>Reason</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}>Order total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}>Expected</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(group.orders || []).map((order) => {
                              const locationLabel = order.location_name || order.location || '-';
                              return (
                                <TableRow
                                  key={`${order.source}-${order.order}-${order.reason}`}
                                  hover
                                  aria-label={`Order #${order.order || '-'}`}
                                >
                                  <TableCell sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 1, sm: 'auto' }, whiteSpace: 'nowrap' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                      <Typography sx={{ color: '#0f172a', fontSize: '0.8rem', fontWeight: 900 }}>
                                        Order #{order.order || '-'}
                                      </Typography>
                                      <Typography sx={{ display: { xs: 'block', sm: 'none' }, maxWidth: '55%', color: '#475569', fontSize: '0.75rem', fontWeight: 800, textAlign: 'right', wordBreak: 'break-word' }}>
                                        {locationLabel}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ display: { xs: 'none !important', sm: 'table-cell !important' }, color: '#475569', fontSize: '0.76rem' }}>
                                    {locationLabel}
                                  </TableCell>
                                  <TableCell sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 3, sm: 'auto' }, whiteSpace: 'nowrap', color: '#475569', fontSize: '0.76rem' }}>
                                    <Typography component="span" sx={{ display: { xs: 'inline', sm: 'none' }, color: '#94a3b8', fontSize: '0.68rem', fontWeight: 800, mr: 0.5 }}>
                                      Decision
                                    </Typography>
                                    {this.formatDateTime(order.decision_on)}
                                  </TableCell>
                                  <TableCell sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' }, minWidth: { sm: 180 } }}>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Chip
                                        size="small"
                                        color={order.source === 'logged' ? 'warning' : 'default'}
                                        label={order.source === 'logged'
                                          ? 'Logged'
                                          : order.source === 'inferred' ? 'Detected' : order.source}
                                      />
                                      <Typography sx={{ color: '#475569', fontSize: '0.75rem' }}>
                                        {order.reason_label || order.reason}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="right" sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 4, sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' }, whiteSpace: 'nowrap', fontSize: '0.76rem' }}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      spacing={1.25}
                                      sx={{ display: { xs: 'flex', sm: 'none' } }}
                                    >
                                      <Box>
                                        <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 800 }}>
                                          Order total
                                        </Typography>
                                        <Typography sx={{ color: '#475569', fontSize: '0.76rem' }}>
                                          {this.formatMoney(order.order_total)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 800 }}>
                                          Expected credit
                                        </Typography>
                                        <Typography sx={{ color: '#92400e', fontSize: '0.76rem', fontWeight: 900 }}>
                                          {this.formatMoney(order.expected_reward)}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                      {this.formatMoney(order.order_total)}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ display: { xs: 'none !important', sm: 'table-cell !important' }, whiteSpace: 'nowrap', color: '#92400e', fontSize: '0.76rem', fontWeight: 900 }}>
                                    {this.formatMoney(order.expected_reward)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={this.closeRewardExceptions} sx={{ textTransform: 'none', fontWeight: 850 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderDailySummary = () => {
    const rows = this.getDailySummaryRows();

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Daily credit summary',
          `All clients · ${PERIOD_LABELS[this.state.periodMode]}`
        )}

        <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, pt: 1.25, flex: 1, minHeight: 0 }}>
          <TableContainer
            sx={{
              height: '100%',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              borderRadius: '12px',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Rewarded</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Used</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Net</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Transactions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ py: 3, textAlign: 'center', color: '#64748b' }}>
                        No credit activity found for the selected period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {rows.map((row) => (
                  <TableRow key={row.date} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155', fontWeight: 800 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => this.openRewardExceptions(row.date)}
                        aria-label={`View reward exceptions for ${row.date}`}
                        sx={{
                          minWidth: 0,
                          p: 0,
                          color: '#1d4ed8',
                          fontSize: 'inherit',
                          fontWeight: 900,
                          textTransform: 'none',
                        }}
                      >
                        {row.date}
                      </Button>
                    </TableCell>

                    <TableCell align="right" sx={{ color: '#166534', fontWeight: 950, whiteSpace: 'nowrap' }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => this.openRewardExceptions(row.date)}
                        aria-label={`View reward exceptions from rewarded amount for ${row.date}`}
                        sx={{
                          minWidth: 0,
                          p: 0,
                          color: '#166534',
                          fontSize: 'inherit',
                          fontWeight: 950,
                          textTransform: 'none',
                        }}
                      >
                        {this.formatMoney(row.rewarded)}
                      </Button>
                    </TableCell>

                    <TableCell align="right" sx={{ color: '#991b1b', fontWeight: 950, whiteSpace: 'nowrap' }}>
                      {this.formatMoney(row.used)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        color: row.net >= 0 ? '#166534' : '#991b1b',
                        fontWeight: 950,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {this.formatMoney(row.net, true)}
                    </TableCell>

                    <TableCell align="right" sx={{ color: '#334155', fontWeight: 850 }}>
                      {this.formatCount(row.transactions)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    );
  };

  renderClientTransactions = () => {
    const {
      history,
      loadingHistory,
      selectedClient,
      searchEmail,
      historyFilter,
    } = this.state;

    const rows = this.getClientTransactionRows();

    const filters = [
      { value: 'all', label: 'All' },
      { value: 'rewarded', label: 'Rewarded' },
      { value: 'used', label: 'Used' },
      { value: 'manual', label: 'Manual' },
    ];

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Client credit history',
          `${searchEmail || selectedClient?.email || 'Selected client'} · last 20 transactions`,
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ overflowX: 'auto' }}>
            {filters.map((item) => (
              <Chip
                key={item.value}
                size="small"
                label={item.label}
                clickable
                onClick={() => this.setState({ historyFilter: item.value })}
                sx={{
                  fontWeight: 850,
                  bgcolor: historyFilter === item.value ? '#0f172a' : '#f1f5f9',
                  color: historyFilter === item.value ? '#ffffff' : '#334155',
                  flexShrink: 0,
                }}
              />
            ))}

            {loadingHistory ? (
              <CircularProgress size={20} />
            ) : (
              <Chip
                size="small"
                label={`${this.formatCount(rows.length)}/${this.formatCount(history.length)}`}
                sx={{ fontWeight: 850, bgcolor: '#eff6ff', color: '#1e3a8a', flexShrink: 0 }}
              />
            )}
          </Stack>
        )}

        <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, pt: 1.25, flex: 1, minHeight: 0 }}>
          <Box sx={{ display: { xs: 'none', md: 'block' }, height: '100%' }}>
            <TableContainer
              sx={{
                height: '100%',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                borderRadius: '12px',
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Created by</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Note</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loadingHistory && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" sx={{ py: 3, textAlign: 'center', color: '#64748b' }}>
                          No client transactions found for the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {rows.map((row, index) => {
                    const rowValue = this.getRowValue(row);
                    const isPositive = rowValue > 0;
                    const isNegative = rowValue < 0;
                    const rowType = this.getRowType(row);

                    return (
                      <TableRow key={row.iref || `${row.datetime}-${index}`} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155' }}>
                          {this.formatDateTime(row.datetime || row.createdOn)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={CREDIT_TYPES[rowType] || row.type || '-'}
                            sx={{
                              fontWeight: 850,
                              bgcolor: isPositive ? '#f0fdf4' : isNegative ? '#fef2f2' : '#f1f5f9',
                              color: isPositive ? '#166534' : isNegative ? '#991b1b' : '#334155',
                            }}
                          />
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 950,
                            color: isPositive ? '#166534' : isNegative ? '#991b1b' : '#334155',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {this.formatMoney(row.value, isPositive)}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.order || '-'}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.createdBy_name || '-'}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {STATUS_LABELS[row.status] || row.status || '-'}
                        </TableCell>

                        <TableCell sx={{ minWidth: 220 }}>
                          {row.note || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Stack
            spacing={1}
            sx={{
              display: { xs: 'flex', md: 'none' },
              height: '100%',
              minHeight: 0,
              overflowY: 'auto',
              pr: 0.25,
            }}
          >
            {!loadingHistory && rows.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  color: '#64748b',
                  bgcolor: '#f8fafc',
                  borderRadius: '14px',
                }}
              >
                <Typography variant="body2">
                  No client transactions found for the selected filters.
                </Typography>
              </Box>
            ) : null}

            {rows.map((row, index) => {
              const rowValue = this.getRowValue(row);
              const isPositive = rowValue > 0;
              const isNegative = rowValue < 0;
              const rowType = this.getRowType(row);

              return (
                <Paper
                  key={row.iref || `${row.datetime}-${index}`}
                  elevation={0}
                  sx={{
                    p: 1.1,
                    borderRadius: '14px',
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 950, color: '#020617', fontSize: '0.86rem' }}>
                        {CREDIT_TYPES[rowType] || row.type || '-'}
                      </Typography>

                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {this.formatDateTime(row.datetime || row.createdOn)}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 950,
                        color: isPositive ? '#166534' : isNegative ? '#991b1b' : '#334155',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {this.formatMoney(row.value, isPositive)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.6, mb: row.note ? 0.65 : 0 }}>
                    <Chip size="small" label={row.order ? `Order ${row.order}` : this.isManualRow(row) ? 'Manual' : 'System'} />
                    <Chip size="small" label={STATUS_LABELS[row.status] || row.status || 'Status -'} />

                    {row.createdBy_name ? (
                      <Chip size="small" label={row.createdBy_name} />
                    ) : null}
                  </Stack>

                  {row.note ? (
                    <Typography sx={{ color: '#475569', fontSize: '0.75rem' }}>
                      {row.note}
                    </Typography>
                  ) : null}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    );
  };

  renderActivitySection = () => {
    const { selectedClient } = this.state;

    if (selectedClient) {
      return this.renderClientTransactions();
    }

    return this.renderDailySummary();
  };

  render() {
    const {
      selectedClient,
      periodMode,
      loadingHistory,
      error,
      success,
      creditBalanceAmount,
      creditBalanceDate,
      creditBalanceEstimated,
      loadingCreditBalance,
      creditBalanceError,
    } = this.state;

    const summary = selectedClient
      ? this.getHistorySummary(this.state.history)
      : this.state.allClientSummary;
    const clientSummary = selectedClient
      ? this.getHistorySummary(this.state.history)
      : this.state.allClientSummary;

    const pageSx = {
      height: {
        xs: 'auto',
        md: 'calc(100vh - 88px)',
      },
      minHeight: {
        xs: '100vh',
        md: 640,
      },
      flexGrow: 1,
      width: {
        xs: 'calc(100vw - 8px)',
        sm: '100%',
      },
      maxWidth: {
        xs: 'calc(100vw - 8px)',
        sm: '100%',
      },
      ml: {
        xs: 'calc(50% - 50vw + 4px)',
        sm: 0,
      },
      overflow: {
        xs: 'visible',
        md: 'hidden',
      },
      pt: { xs: 0.75, sm: 1.5, md: 2 },
      pb: { xs: 1, sm: 2, md: 2 },
      px: { xs: 0.5, sm: 1, md: 2 },
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',

      '& .MuiGrid-item': {
        minWidth: 0,
      },
    };

    const headerSx = {
      width: '100%',
      minWidth: 0,
      p: { xs: 1.25, sm: 1.5, md: 1.75 },
      borderRadius: { xs: '14px', sm: '16px' },
      border: '1px solid rgba(15, 23, 42, 0.06)',
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
      bgcolor: '#ffffff',
      boxSizing: 'border-box',
      flexShrink: 0,
    };

    return (
      <Box component="main" sx={pageSx}>
        {this.renderTopClientsModal()}
        {this.renderRewardExceptionsModal()}
        <Stack
          spacing={{ xs: 0.75, md: 1.25 }}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Paper elevation={0} sx={headerSx}>
            <Grid container spacing={{ xs: 1, md: 1.25 }} alignItems="center">
              <Grid item xs={12} lg={3}>
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    textAlign: 'left',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{
                      mb: 0.25,
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 950,
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                        lineHeight: 1.2,
                        color: '#020617',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Credit Management
                    </Typography>

                    {loadingHistory ? <CircularProgress size={18} /> : null}
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#475569',
                      fontSize: { xs: '0.72rem', sm: '0.78rem' },
                      lineHeight: 1.25,
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    Rewarded, used, and client credit control.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Grid container spacing={0.75}>
                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Rewarded',
                      this.formatMoney(summary.rewarded),
                      'credit received',
                      'positive'
                    )}
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Used',
                      this.formatMoney(summary.used),
                      'credit spent',
                      'negative'
                    )}
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Net',
                      this.formatMoney(summary.net, true),
                      'movement',
                      summary.net >= 0 ? 'positive' : 'negative'
                    )}
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Total credit',
                      loadingCreditBalance ? '…' : this.formatMoney(creditBalanceAmount),
                      `All clients · ${creditBalanceDate || '—'}`,
                      'neutral',
                      creditBalanceEstimated
                        ? 'Estimated at the end of this day in Europe/Bucharest: the sum of current client credit balances minus all signed credit-history movements recorded after this day. Missing or deleted history can affect the estimate.'
                        : 'Sum of current credit balances for all non-erased client accounts. This is a credit balance, not an accounting provision.'
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={3}>
                <Stack
                  direction={{ xs: 'column', sm: 'row', lg: 'column' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center', lg: 'flex-end' }}
                >
                  <Chip
                    label={selectedClient ? 'Selected client' : 'All clients'}
                    sx={{
                      fontWeight: 900,
                      bgcolor: selectedClient ? '#eff6ff' : '#f1f5f9',
                      color: selectedClient ? '#1e3a8a' : '#334155',
                      alignSelf: { xs: 'flex-start', sm: 'center', lg: 'flex-end' },
                    }}
                  />

                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={periodMode}
                    onChange={this.handlePeriodChange}
                    aria-label="Credit report period"
                    sx={{
                      alignSelf: { xs: 'flex-start', sm: 'center', lg: 'flex-end' },
                      flexShrink: 0,
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',

                      '& .MuiToggleButtonGroup-grouped': {
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                      },

                      '& .MuiToggleButton-root': {
                        minWidth: { xs: 34, sm: 38 },
                        px: { xs: 0.75, sm: 1 },
                        py: { xs: 0.35, sm: 0.45 },
                        fontSize: { xs: '0.66rem', sm: '0.72rem' },
                        fontWeight: 850,
                        textTransform: 'none',
                        color: '#334155',
                        borderColor: 'rgba(15, 23, 42, 0.08)',

                        '&.Mui-selected': {
                          bgcolor: '#0f172a',
                          color: '#fff',

                          '&:hover': {
                            bgcolor: '#111827',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="last_7d">7d</ToggleButton>
                    <ToggleButton value="this_month">Month</ToggleButton>
                    <ToggleButton
                      ref={this.customPeriodButton}
                      value="custom"
                      aria-haspopup="dialog"
                      aria-expanded={this.state.customPeriodOpen}
                      aria-controls={this.state.customPeriodOpen ? 'credit-custom-period' : undefined}
                      onClick={periodMode === 'custom' ? (event) => this.handlePeriodChange(event, 'custom') : undefined}
                    >
                      Custom
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {periodMode === 'custom' && (
                    <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                      {formatCreditDateTime(this.state.dateFrom).slice(0, 10)} – {formatCreditDateTime(this.state.dateTo).slice(0, 10)}
                    </Typography>
                  )}
                  <CustomPeriodPopover
                    id="credit-custom-period"
                    open={this.state.customPeriodOpen}
                    anchorEl={this.customPeriodButton.current}
                    onClose={() => this.setState({ customPeriodOpen: false })}
                    dateFrom={this.state.draftDateFrom}
                    dateTo={this.state.draftDateTo}
                    onDateFromChange={(value) => this.setState({ draftDateFrom: value, customPeriodError: '' })}
                    onDateToChange={(value) => this.setState({ draftDateTo: value, customPeriodError: '' })}
                    onApply={this.handleCustomPeriodApply}
                    error={this.state.customPeriodError}
                    description="Choose up to 121 inclusive days."
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: '12px', flexShrink: 0 }}>
              {error}
            </Alert>
          ) : null}

          {creditBalanceError ? (
            <Alert severity="error" sx={{ borderRadius: '12px', flexShrink: 0 }} action={<Button color="inherit" size="small" onClick={this.loadCreditBalance}>Retry</Button>}>
              {creditBalanceError}
            </Alert>
          ) : null}

          {success ? (
            <Alert severity="success" sx={{ borderRadius: '12px', flexShrink: 0 }}>
              {success}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(0, 2fr) minmax(320px, 1fr)',
              },
              gap: { xs: 0.75, md: 1.25 },
              flex: {
                xs: 'initial',
                md: 1,
              },
              minHeight: {
                xs: 'initial',
                md: 0,
              },
              width: '100%',
            }}
          >
            <Stack
              spacing={{ xs: 0.75, md: 1.25 }}
              sx={{
                height: {
                  xs: 'auto',
                  md: '100%',
                },
                minHeight: 0,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: {
                    xs: 300,
                    md: '40%',
                  },
                  minHeight: {
                    xs: 300,
                    md: 0,
                  },
                  minWidth: 0,
                }}
              >
                {this.renderEvolutionChart()}
              </Box>

              <Box
                sx={{
                  height: {
                    xs: selectedClient ? 560 : 440,
                    md: '60%',
                  },
                  minHeight: {
                    xs: selectedClient ? 560 : 440,
                    md: 0,
                  },
                  minWidth: 0,
                }}
              >
                {this.renderActivitySection()}
              </Box>
            </Stack>

            <Box
              sx={{
                height: {
                  xs: 640,
                  md: '100%',
                },
                minHeight: 0,
                minWidth: 0,
              }}
            >
              {this.renderClientPanel(clientSummary)}
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  }
}

export default ClientCreditManagement;
